/**
 * Module-level bridge giving non-React handlers a Microsoft Graph bearer token.
 *
 * Mirrors ``confirm-bridge`` / ``toast-bridge``: the handler map in
 * ``handlers.ts`` lives outside the React tree, so it cannot read
 * ``useAuthState``. ``GraphAccessBridge`` registers a provider on mount and
 * handlers pull the current token through ``getGraphAccess``.
 *
 * Currently used by ``calendar.open`` / ``calendar.open_teams`` for the CM-01
 * Graph fallback on hosts with no native appointment form.
 */

export interface GraphAccess {
  /** Current Graph access token, or null when the user is not connected. */
  readonly token: string | null;
  /** Re-acquires the token after a 401. Absent when nothing has registered. */
  readonly refresh?: () => Promise<string | null>;
}

type GraphAccessProvider = () => GraphAccess;

let provider: GraphAccessProvider | null = null;

export function registerGraphAccess(next: GraphAccessProvider): () => void {
  provider = next;
  return () => {
    if (provider === next) provider = null;
  };
}

/** Handlers call this. Reports "not connected" when no provider is registered. */
export function getGraphAccess(): GraphAccess {
  return provider ? provider() : { token: null };
}
